/**
 * GlobalSearchModal — command palette search modal using cmdk + Radix Dialog.
 *
 * Auto-focuses input on open, closes on Escape, restores focus on close.
 */
import { useCallback, useEffect, useRef } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Command } from 'cmdk';
import { Search, Loader2, Users, MessageSquare, Calendar, BookOpen, FileText, DollarSign, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useGlobalSearch } from './GlobalSearchProvider';
import { useSearch } from './useSearch';
import type { GroupedSearchResults, ResultCategory, SearchableEntityType, SearchResultItem } from './types';

export function GlobalSearchModal() {
  const { isOpen, closeSearch } = useGlobalSearch();
  const { query, setQuery, results, isLoading, error, retry } = useSearch();
  const navigate = useNavigate();
  const previouslyFocusedRef = useRef<Element | null>(null);

  useEffect(() => {
    if (isOpen) {
      previouslyFocusedRef.current = document.activeElement;
    }
  }, [isOpen]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        closeSearch();
        setQuery('');
        restoreFocus(previouslyFocusedRef.current);
      }
    },
    [closeSearch, setQuery],
  );

  const handleSelect = useCallback(
    (item: SearchResultItem) => {
      closeSearch();
      setQuery('');
      navigate(item.url);
    },
    [closeSearch, setQuery, navigate],
  );

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={cn(
            'fixed top-[15%] left-[50%] z-50 w-full max-w-[calc(100%-2rem)] translate-x-[-50%] sm:max-w-lg',
            'bg-background rounded-lg border border-border shadow-raised',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          )}
          aria-label="Global search"
          onEscapeKeyDown={() => handleOpenChange(false)}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            restoreFocus(previouslyFocusedRef.current);
          }}
        >
          <DialogPrimitive.Title className="sr-only">Global Search</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Search across people, sponsors, expenses, appearances, and social media posts.
          </DialogPrimitive.Description>

          <Command className="flex h-full w-full flex-col overflow-hidden rounded-lg" shouldFilter={false}>
            {/* Search input */}
            <div className="flex items-center border-b border-border px-3">
              <Search className="mr-2 size-4 shrink-0 text-muted-foreground" />
              <Command.Input
                placeholder="Search..."
                value={query}
                onValueChange={setQuery}
                autoFocus
                className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 font-sans"
              />
              {isLoading && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
            </div>

            {/* Results list */}
            <Command.List className="max-h-[300px] overflow-y-auto overflow-x-hidden p-2">
              {error && (
                <div className="flex flex-col items-center gap-2 py-6 px-4">
                  <p className="text-sm text-muted-foreground text-center">{error.message}</p>
                  <button
                    onClick={retry}
                    className="text-xs text-primary hover:underline font-sans"
                  >
                    Retry
                  </button>
                </div>
              )}

              {!error && !isLoading && query.length >= 2 && results && Object.keys(results).length === 0 && (
                <div className="py-6 text-center text-sm text-muted-foreground font-sans">
                  No results found for &apos;{query}&apos;
                </div>
              )}

              {!error && results && Object.keys(results).length > 0 && (
                <SearchResults results={results} onSelect={handleSelect} />
              )}

              {!error && query.length < 2 && (
                <div className="py-6 text-center text-sm text-muted-foreground font-sans">
                  Type at least 2 characters to search
                </div>
              )}
            </Command.List>
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function SearchResults({
  results,
  onSelect,
}: {
  results: GroupedSearchResults;
  onSelect: (item: SearchResultItem) => void;
}) {
  const categories = Object.keys(results) as ResultCategory[];
  if (categories.length === 0) return null;

  return (
    <>
      {categories.map((category) => {
        const items = results[category];
        if (!items || items.length === 0) return null;

        return (
          <div key={category} className="mb-3">
            <div className="px-2 py-1.5 text-[10px] font-sans uppercase tracking-widest text-muted-foreground font-semibold">
              {category}
            </div>
            {items.map((item) => (
              <Command.Item
                key={item.id}
                value={`${item.name}-${item.id}`}
                onSelect={() => onSelect(item)}
                className="relative flex cursor-pointer select-none items-center gap-3 rounded-md px-2 py-2 text-sm outline-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground"
              >
                <EntityIcon entityType={item.entityType} />
                <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                  <span className="font-sans text-sm font-medium text-foreground truncate">{item.name}</span>
                  {item.description && (
                    <span className="font-sans text-[11px] text-muted-foreground truncate">{item.description}</span>
                  )}
                </div>
                <span className="font-sans text-[9px] uppercase tracking-widest text-muted-foreground shrink-0">
                  {entityTypeLabel(item.entityType)}
                </span>
              </Command.Item>
            ))}
          </div>
        );
      })}
    </>
  );
}

function EntityIcon({ entityType }: { entityType: SearchableEntityType }) {
  const iconClass = 'size-4 text-muted-foreground shrink-0';
  switch (entityType) {
    case 'person': return <Users className={iconClass} />;
    case 'chat': return <MessageSquare className={iconClass} />;
    case 'event': return <Calendar className={iconClass} />;
    case 'guidebook': return <BookOpen className={iconClass} />;
    case 'document': return <FileText className={iconClass} />;
    case 'expense': return <DollarSign className={iconClass} />;
    case 'inquiry': return <Mail className={iconClass} />;
    default: return <Search className={iconClass} />;
  }
}

function entityTypeLabel(entityType: SearchableEntityType): string {
  switch (entityType) {
    case 'person': return 'Person';
    case 'chat': return 'Message';
    case 'event': return 'Event';
    case 'guidebook': return 'Guide';
    case 'document': return 'Doc';
    case 'expense': return 'Finance';
    case 'inquiry': return 'Inquiry';
    default: return '';
  }
}

function restoreFocus(element: Element | null) {
  if (!element || !document.contains(element)) {
    document.body.focus();
    return;
  }
  if (element instanceof HTMLElement) {
    try { element.focus(); } catch { document.body.focus(); }
  } else {
    document.body.focus();
  }
}
